import { validBillingPaymentMethod, type BillingPaymentMethodReference } from "./billingPaymentMethod.ts";

export type BillingTerm = { durationDays?: number | null; periodUnit?: "DAY" | "WEEK" | "MONTH" | "YEAR"; periodCount?: number; periodTimezone?: string };
export type BillingPrice = BillingTerm & { priceId: string; label: string; currency: string; amount: string };
export type BillingTaxAuthority = { id: string; label: string; ratePercent: string; mode: "EXCLUSIVE" | "INCLUSIVE" | "NONE" };
export type BillingPriceQuote = BillingTerm & {
  quoteId: string; expiresAtEpochMillis: number; priceId: string; priceLabel: string; currency: string;
  subtotal: string; discount: string; tax: string; total: string;
  taxAuthorityId: string; taxAuthorityLabel: string; taxMode: BillingTaxAuthority["mode"]; taxRatePercent: string;
  promoCode: string | null; localSimulation: true;
};
export type BillingPaymentQuote = BillingPaymentMethodReference & {
  quoteId: string; expiresAtEpochMillis: number; subscriptionId: string; paymentAmount: string; paymentCurrency: string;
  invoiceAmount: string; invoiceCurrency: string; exchangeRate: string; rateDateEpochMillis: number; localSimulation: true;
};

export const billingText = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 500;
export const billingDecimal = (value: unknown): value is string => typeof value === "string" && /^(0|[1-9]\d{0,29})(\.\d{1,9})?$/.test(value);
export const billingExchangeRate = (value: unknown): value is string => typeof value === "string" && /^(0|[1-9]\d{0,29})(\.\d{1,12})?$/.test(value) && /[1-9]/.test(value);
export const billingCurrency = (value: unknown): value is string => typeof value === "string" && /^[A-Z]{3}$/.test(value);
export const billingDate = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 8_640_000_000_000_000;
const term = (value: unknown) => typeof value === "number" && Number.isSafeInteger(value) && value > 0;
export function validBillingTerm(value: BillingTerm): boolean {
  if(value.periodUnit===undefined && value.periodCount===undefined) return term(value.durationDays);
  return ["DAY","WEEK","MONTH","YEAR"].includes(value.periodUnit||"") && term(value.periodCount) &&
    (value.durationDays==null || (value.periodUnit==="DAY" && value.durationDays===value.periodCount));
}
export function billingTermLabel(value: BillingTerm, w:(ar:string,en:string)=>string): string {
  const unit=value.periodUnit||"DAY", count=value.periodCount??value.durationDays;
  return `${count} ${unit==="MONTH"?w("شهر تقويمي","calendar month(s)"):unit==="YEAR"?w("سنة تقويمية","calendar year(s)"):unit==="WEEK"?w("أسبوع","week(s)"):w("يوم","day(s)")}`;
}
const taxMode = (value: unknown) => ["EXCLUSIVE", "INCLUSIVE", "NONE"].includes(value as string);
const positive = (value: unknown): value is string => billingDecimal(value) && /[1-9]/.test(value);

export function isBillingPrice(value: unknown): value is BillingPrice {
  if (!value || typeof value !== "object") return false;
  const item = value as BillingPrice;
  return billingText(item.priceId) && billingText(item.label) && billingCurrency(item.currency) && positive(item.amount) && validBillingTerm(item);
}
export function isBillingTaxAuthority(value: unknown): value is BillingTaxAuthority {
  if (!value || typeof value !== "object") return false;
  const item = value as BillingTaxAuthority;
  return billingText(item.id) && billingText(item.label) && billingDecimal(item.ratePercent) && taxMode(item.mode);
}
export function parseBillingPriceQuote(value: unknown): BillingPriceQuote {
  if (!value || typeof value !== "object") throw new Error("Invalid price quote");
  const quote = value as BillingPriceQuote;
  if (![quote.quoteId, quote.priceId, quote.priceLabel, quote.taxAuthorityId, quote.taxAuthorityLabel].every(billingText) ||
      !billingDate(quote.expiresAtEpochMillis) || !billingCurrency(quote.currency) || !validBillingTerm(quote) ||
      ![quote.subtotal, quote.discount, quote.tax, quote.total, quote.taxRatePercent].every(billingDecimal) ||
      !taxMode(quote.taxMode) || !(quote.promoCode === null || billingText(quote.promoCode)) || quote.localSimulation !== true)
    throw new Error("Invalid price quote");
  return quote;
}
export function parseBillingPaymentQuote(value: unknown, options: { requireMethod?: boolean } = {}): BillingPaymentQuote {
  if (!value || typeof value !== "object") throw new Error("Invalid payment quote");
  const quote = value as BillingPaymentQuote;
  if (![quote.quoteId, quote.subscriptionId].every(billingText) || !billingDate(quote.expiresAtEpochMillis) ||
      !billingDate(quote.rateDateEpochMillis) || ![quote.paymentCurrency, quote.invoiceCurrency].every(billingCurrency) ||
      ![quote.paymentAmount, quote.invoiceAmount].every(positive) || !billingExchangeRate(quote.exchangeRate) || quote.localSimulation !== true ||
      !validBillingPaymentMethod(quote, options.requireMethod))
    throw new Error("Invalid payment quote");
  return quote;
}
export function billingQuoteExpired(quote: { expiresAtEpochMillis: number }, now = Date.now()): boolean {
  return quote.expiresAtEpochMillis <= now;
}

/** Compare scaled integer strings. No monetary amount or rate is converted to Number. */
export function validInvoicePaymentAmount(amount: string, remaining: string): boolean {
  if (!positive(amount) || !billingDecimal(remaining)) return false;
  const scale = Math.max((amount.split(".")[1] || "").length, (remaining.split(".")[1] || "").length);
  const units = (value: string) => { const [whole, fraction = ""] = value.split("."); return BigInt(whole + fraction.padEnd(scale, "0")); };
  return units(amount) <= units(remaining);
}
